// File: frontend/src/components/modals/QuoteModal.js

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import styles from './QuoteModal.module.css';

const QuoteModal = ({ isOpen, onClose, onOpenSignIn, onOpenSignUp }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo({ top: scrollY, behavior: 'instant' });
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleUploadPolicy = () => {
    onClose();
    if (user) {
      navigate('/dashboard/upload');
    } else {
      sessionStorage.setItem('redirect_after_auth', '/dashboard/upload');
      
      if (onOpenSignIn && typeof onOpenSignIn === 'function') {
        onOpenSignIn();
      } else {
        navigate('/', { state: { openSignIn: true } });
      }
    }
  };

  const handleManualQuote = () => {
    onClose();
    if (user) {
      navigate('/quote/form');
    } else {
      sessionStorage.setItem('redirect_after_auth', '/quote/form');
      
      if (onOpenSignUp && typeof onOpenSignUp === 'function') {
        onOpenSignUp();
      } else {
        navigate('/', { state: { openSignUp: true } });
      }
    }
  };

  return createPortal(
    <div className={styles.modalBackdrop} onClick={handleBackdropClick}>
      <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
        <button 
          className={styles.closeButton} 
          onClick={onClose}
          aria-label="Close"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
        
        <div className={styles.modalHeader}>
          <h2 className={styles.title}>Get Your Insurance Quote</h2>
          <p className={styles.subtitle}>Choose how you'd like to get started</p>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.options}>
            {/* Option 1: Manual Quote (Coming Soon) */}
            <div className={`${styles.optionCard} ${styles.disabled}`}>
              <div className={styles.comingSoonBadge}>Coming Soon</div>
              <div className={styles.optionIcon}>📝</div>
              <h3 className={styles.optionTitle}>Fill Out Quote Form</h3>
              <p className={styles.optionDescription}>
                Manually enter your information to get a quote
              </p>
            </div>

            {/* Option 2: Upload Policy (Active) */}
            <button className={styles.optionCard} onClick={handleUploadPolicy}>
              <div className={styles.optionIcon}>📄</div>
              <h3 className={styles.optionTitle}>Upload Your Policy</h3>
              <p className={styles.optionDescription}>
                Get instant quotes by uploading your current insurance policy
              </p>
              <span className={styles.optionButton}>
                {user ? 'Upload Policy' : 'Sign In & Upload'}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default QuoteModal;